/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Box,
    Button,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon, BrandLogo} from '@consteed/overrides/components/icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import UpdateResetPasswordFields from '@consteed/overrides/components/forms/update-reset-password-fields'

const UpdateResetPasswordForm = ({submitForm, clickSignIn = noop, form, submittedEmail}) => {
    const styles = useMultiStyleConfig('Login')
    const intl = useIntl()
    return (
        <Fragment>
            <Stack
                justify="center"
                align="center"
                spacing={8}
                data-testid="update-reset-password-frag"
            >
                <Box
                    aria-label={intl.formatMessage({
                        id: 'header.button.assistive_msg.logo',
                        defaultMessage: 'Logo'
                    })}
                    {...styles.logoContainer}
                >
                    <BrandLogo {...styles.logo} />
                </Box>
                <Stack spacing={2}>
                    <Text align="center" textStyle={'mediumXlarge'}>
                        <FormattedMessage
                            defaultMessage="Update Password"
                            id="update_reset_password_form.title.update_password"
                        />
                    </Text>
                    <Text align="center" fontSize="sm">
                        <FormattedMessage
                            defaultMessage="You will receive an email at <b>{email}</b> with your reset token to reset your password shortly. Please stay here to input your token, the token is only valid for 10 minutes."
                            id="auth_modal.password_reset_success.info.will_email_shortly"
                            values={{
                                email: submittedEmail.current,

                                b: (chunks) => <b>{chunks}</b>
                            }}
                        />
                    </Text>
                    <Text fontSize="sm" align="center" color="gray.700">
                        <FormattedMessage
                            defaultMessage="Enter your the password reset token send to your email and a new password"
                            id="update_reset_password_form.message.enter_token_and_password"
                        />
                    </Text>
                </Stack>
            </Stack>
            <form
                onSubmit={form.handleSubmit(submitForm)}
                data-testid="sf-update-reset-password-form"
            >
                <Stack paddingTop={8} spacing={8} paddingLeft={4} paddingRight={4}>
                    {form.formState.errors?.global && (
                        <Alert status="error">
                            <AlertIcon color="red.600" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}
                    <UpdateResetPasswordFields form={form} />
                    <Stack spacing={6}>
                        <Button
                            type="submit"
                            variant={'primary'}
                            onClick={() => form.clearErrors('global')}
                            isLoading={form.formState.isSubmitting}
                            {...styles.submitBtn}
                        >
                            <FormattedMessage
                                defaultMessage="Update Password"
                                id="update_reset_password_form.button.update_password"
                            />
                        </Button>

                        <Stack direction="row" spacing={1} justify="center">
                            <Text fontSize="sm">
                                <FormattedMessage
                                    defaultMessage="Or return to"
                                    id="update_reset_password_form.message.return_to_sign_in"
                                    description="Precedes link to return to sign in"
                                />
                            </Text>
                            <Button variant="link" size="sm" onClick={clickSignIn}>
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="update_reset_password_form.action.sign_in"
                                />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

UpdateResetPasswordForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object,
    submittedEmail: PropTypes.shape({current: PropTypes.string})
}

export default UpdateResetPasswordForm
